import { useEffect, useMemo, useState } from "react";
import { useBatchStore } from "../../stores/useBatchStore";
import { useToastStore } from "../../stores/useToastStore";
import { formatApiError } from "../../lib/api-errors";

/** `methodologies/docs/<family>/<version>.md` → `<family>`. */
function familyFromPath(path: string): string {
  const parts = path.replace(/\.[A-Za-z0-9]+$/, "").split("/");
  parts.pop(); // drop the version segment
  return parts[parts.length - 1] ?? path;
}

function commonPrefix(values: string[]): string {
  if (values.length === 0) return "";
  let prefix = values[0];
  for (const v of values) {
    while (prefix && !v.startsWith(prefix)) prefix = prefix.slice(0, -1);
    if (!prefix) break;
  }
  return prefix;
}

/** Conventional-commit-style title, e.g. `docs(factsheet): add govr, houser`.
 *  Scope = the families' shared prefix (trimmed to a word boundary); each name
 *  has that prefix stripped so the list stays terse. */
function suggestPrTitle(files: string[]): string {
  const families = files.map(familyFromPath);
  if (families.length === 0) return "";
  const shared = commonPrefix(families);
  const dash = shared.lastIndexOf("-");
  const prefix = dash >= 0 ? shared.slice(0, dash + 1) : "";
  const scope = prefix.replace(/-$/, "");
  const names = families.map((f) =>
    prefix && f.startsWith(prefix) ? f.slice(prefix.length) : f,
  );
  const head = scope ? `docs(${scope})` : "docs";
  return `${head}: add ${names.join(", ")}`;
}

/**
 * Floating PR button — a squared action in the bottom-right that appears once
 * the current batch has unsubmitted doc changes staged on its draft branch.
 * Clicking opens a popover to review/remove staged docs, discard the batch, or
 * open one PR for the whole batch.
 */
export function PrFab() {
  const files = useBatchStore((s) => s.files);
  const submitting = useBatchStore((s) => s.submitting);
  const refresh = useBatchStore((s) => s.refresh);
  const remove = useBatchStore((s) => s.remove);
  const discard = useBatchStore((s) => s.discard);
  const submit = useBatchStore((s) => s.submit);
  const addToast = useToastStore((s) => s.addToast);

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [titleEdited, setTitleEdited] = useState(false);

  // Auto-filled title tracks the staged docs until the user edits it.
  const suggestedTitle = useMemo(() => suggestPrTitle(files), [files]);
  const effectiveTitle = titleEdited ? title : suggestedTitle;

  // Reconcile with the server on mount so the button reflects docs staged in a
  // previous session (survives reloads).
  useEffect(() => {
    refresh();
  }, [refresh]);

  if (files.length === 0) return null;

  const onToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) refresh();
  };

  const onSubmit = async () => {
    try {
      const pr = await submit(effectiveTitle.trim() || undefined);
      setOpen(false);
      setTitle("");
      setTitleEdited(false);
      if (pr?.url) {
        addToast(`opened PR #${pr.number}`, "success");
        window.open(pr.url, "_blank", "noopener");
      } else {
        addToast("opened PR", "success");
      }
    } catch (e) {
      addToast(formatApiError(e), "error");
    }
  };

  const onDiscard = async () => {
    if (
      !window.confirm(
        `Discard all ${files.length} staged doc(s)? This deletes the draft branch and cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await discard();
      setOpen(false);
      setTitle("");
      setTitleEdited(false);
      addToast("discarded staged changes", "success");
    } catch (e) {
      addToast(formatApiError(e), "error");
    }
  };

  return (
    <div style={styles.wrap}>
      {open && (
        <div style={styles.panel} role="dialog" aria-label="Open pull request">
          <div style={styles.header}>
            <span style={styles.headerTitle}>{files.length} doc(s) staged</span>
            <button
              style={styles.closeBtn}
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              {"\u00d7"}
            </button>
          </div>
          <ul style={styles.list}>
            {files.map((path) => (
              <li key={path} style={styles.item}>
                <span style={styles.itemPath} title={path}>
                  {path}
                </span>
                <button
                  style={styles.removeBtn}
                  onClick={() => remove(path)}
                  aria-label={`Remove ${path}`}
                  disabled={submitting}
                >
                  {"\u00d7"}
                </button>
              </li>
            ))}
          </ul>
          <input
            style={styles.input}
            placeholder="PR title (optional)"
            value={effectiveTitle}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleEdited(true);
            }}
            disabled={submitting}
          />
          <button
            style={styles.submitBtn}
            onClick={onSubmit}
            disabled={submitting}
          >
            {submitting ? "opening\u2026" : "open PR"}
          </button>
          <button
            style={styles.discardBtn}
            onClick={onDiscard}
            disabled={submitting}
          >
            discard all
          </button>
        </div>
      )}
      <button
        style={styles.fab}
        onClick={onToggle}
        aria-label={`Open PR for ${files.length} staged docs`}
        title={`${files.length} doc(s) staged — open PR`}
      >
        <span style={styles.fabLabel}>PR</span>
        <span style={styles.badge}>{files.length}</span>
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    position: "fixed",
    bottom: "var(--space-5)",
    right: "var(--space-5)",
    zIndex: 9998,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "var(--space-3)",
  },
  fab: {
    position: "relative",
    width: 48,
    height: 48,
    borderRadius: "var(--radius-lg)",
    background: "var(--comp-ink)",
    color: "var(--fg-on-deep)",
    border: "1px solid var(--comp-ink)",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  fabLabel: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "1px",
  },
  badge: {
    position: "absolute",
    top: -7,
    right: -7,
    minWidth: 18,
    height: 18,
    padding: "0 4px",
    borderRadius: "var(--radius-md)",
    background: "var(--comp-salmon)",
    color: "var(--fg-on-deep)",
    border: "1px solid var(--comp-paper)",
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  panel: {
    width: 320,
    maxWidth: "80vw",
    background: "var(--comp-paper)",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-lg)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
    padding: "var(--space-3)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--fg-1)",
  },
  closeBtn: {
    fontSize: 16,
    color: "var(--fg-3)",
    border: "none",
    background: "none",
    cursor: "pointer",
    lineHeight: 1,
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    maxHeight: 220,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
    padding: "4px 6px",
    borderRadius: "var(--radius-sm)",
    background: "var(--comp-canvas)",
  },
  itemPath: {
    flex: 1,
    minWidth: 0,
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-2)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    direction: "rtl",
    textAlign: "left",
  },
  removeBtn: {
    fontSize: 14,
    color: "var(--fg-3)",
    border: "none",
    background: "none",
    cursor: "pointer",
    lineHeight: 1,
    flexShrink: 0,
  },
  input: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    padding: "6px 8px",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-md)",
    background: "var(--comp-paper)",
    color: "var(--fg-1)",
  },
  submitBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--fg-on-deep)",
    background: "var(--comp-ink)",
    border: "none",
    borderRadius: "var(--radius-md)",
    padding: "8px 0",
    cursor: "pointer",
  },
  discardBtn: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--comp-salmon)",
    background: "none",
    border: "none",
    padding: "2px 0",
    cursor: "pointer",
  },
};
